import { getCookie, removeCookieOnDomain, setCookieOnDomain } from './cookies'

export const DEVICE_PROFILES_COOKIE = 'arvore_perfis'

export const DEVICE_LAST_USE_COOKIE = 'arvore_ultimo_uso'

export const DEVICE_UNSAVED_COOKIE = 'arvore_sem_salvar'

export const DEVICE_NOTICE_SEEN_COOKIE = 'arvore_perfis_aviso'

export const DEVICE_NOTICE_PENDING_COOKIE = 'arvore_perfis_aviso_pendente'

export const MAX_DEVICE_PROFILES = 4

export const DEVICE_IDLE_MS = 60 * 60 * 1000

const DEVICE_PROFILES_DAYS = 30

export type DeviceProfileKind = 'adult' | 'student'

export type DeviceIdleAction = 'none' | 'pick' | 'logout'

export type DeviceProfile = {
  id: string
  kind: DeviceProfileKind
  name: string
  token: string
  at: number
  detail?: string
}

type TokenPayload = { sub?: unknown; exp?: unknown }

const decodeBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  const binary = atob(padded)
  const escaped = Array.from(
    binary,
    char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`
  ).join('')
  return decodeURIComponent(escaped)
}

const encodeBase64Url = (value: string): string => {
  const binary = encodeURIComponent(value).replace(
    /%([0-9A-F]{2})/g,
    (_, hex) => String.fromCharCode(Number.parseInt(hex, 16))
  )
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const readTokenPayload = (token: string): TokenPayload | null => {
  const part = token.split('.')[1]
  if (!part) return null
  try {
    return JSON.parse(decodeBase64Url(part)) as TokenPayload
  } catch {
    return null
  }
}

export const deviceProfileIdOf = (token: string): string | null => {
  const sub = readTokenPayload(token)?.sub
  if (typeof sub !== 'string' && typeof sub !== 'number') return null
  return String(sub).replace(/^User:/, '')
}

const isAlive = (token: string, now: number): boolean => {
  const exp = readTokenPayload(token)?.exp
  return typeof exp !== 'number' || exp * 1000 > now
}

const isDeviceProfile = (value: unknown): value is DeviceProfile => {
  if (!value || typeof value !== 'object') return false
  const profile = value as Record<string, unknown>
  return (
    typeof profile.id === 'string' &&
    (profile.kind === 'adult' || profile.kind === 'student') &&
    typeof profile.name === 'string' &&
    typeof profile.token === 'string' &&
    typeof profile.at === 'number' &&
    (profile.detail === undefined || typeof profile.detail === 'string')
  )
}

export const parseDeviceProfiles = (
  raw: string | null | undefined,
  now: number = Date.now()
): DeviceProfile[] => {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(decodeBase64Url(raw))
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(isDeviceProfile)
      .filter(profile => isAlive(profile.token, now))
  } catch {
    return []
  }
}

export const serializeDeviceProfiles = (profiles: DeviceProfile[]): string =>
  encodeBase64Url(JSON.stringify(profiles))

export const upsertDeviceProfile = (
  profiles: DeviceProfile[],
  profile: DeviceProfile
): DeviceProfile[] => {
  const others = profiles.filter(item => item.id !== profile.id)
  const keptIds = new Set(
    [...others]
      .sort((a, b) => b.at - a.at)
      .slice(0, MAX_DEVICE_PROFILES - 1)
      .map(item => item.id)
  )
  return [...others.filter(item => keptIds.has(item.id)), profile]
}

export const readDeviceProfiles = (): DeviceProfile[] =>
  parseDeviceProfiles(getCookie(DEVICE_PROFILES_COOKIE))

const writeDeviceProfiles = (profiles: DeviceProfile[]): void => {
  if (profiles.length === 0) {
    removeCookieOnDomain(DEVICE_PROFILES_COOKIE)
    return
  }
  setCookieOnDomain(
    DEVICE_PROFILES_COOKIE,
    serializeDeviceProfiles(profiles),
    DEVICE_PROFILES_DAYS
  )
}

export const parseLastUse = (raw: string | null | undefined): number | null => {
  if (!raw) return null
  const value = Number(raw)
  return Number.isFinite(value) && value > 0 ? value : null
}

export const parseSeenIds = (raw: string | null | undefined): string[] =>
  raw ? raw.split('.').filter(id => /^\d+$/.test(id)) : []

type DeviceIdleInput = {
  now: number
  lastUse: number | null
  profiles: DeviceProfile[]
  currentId: string | null
  unsavedId: string | null
}

export const resolveDeviceIdleAction = ({
  now,
  lastUse,
  profiles,
  currentId,
  unsavedId
}: DeviceIdleInput): DeviceIdleAction => {
  if (!(currentId && lastUse) || now - lastUse < DEVICE_IDLE_MS) return 'none'
  if (unsavedId === currentId) return 'logout'
  const isSaved = profiles.some(profile => profile.id === currentId)
  const hasStudent = profiles.some(profile => profile.kind === 'student')
  return isSaved && hasStudent && profiles.length > 1 ? 'pick' : 'none'
}

export const currentDeviceProfileId = (): string | null => {
  const token = getCookie('access_token')
  return token ? deviceProfileIdOf(token) : null
}

export const readUnsavedProfileId = (): string | null =>
  getCookie(DEVICE_UNSAVED_COOKIE) || null

const hasSeenProfilesNotice = (id: string): boolean =>
  parseSeenIds(getCookie(DEVICE_NOTICE_SEEN_COOKIE)).includes(id)

export const hasPendingProfilesNotice = (): boolean => {
  const id = currentDeviceProfileId()
  return Boolean(
    id &&
      getCookie(DEVICE_NOTICE_PENDING_COOKIE) === id &&
      !hasSeenProfilesNotice(id)
  )
}

export const saveCurrentDeviceProfile = (
  kind: DeviceProfileKind,
  name: string,
  detail?: string | null
): void => {
  const token = getCookie('access_token')
  const id = token ? deviceProfileIdOf(token) : null
  if (!(token && id && name.trim()) || readUnsavedProfileId() === id) return
  const saved = readDeviceProfiles()
  const next = upsertDeviceProfile(saved, {
    id,
    kind,
    name: name.trim(),
    token,
    at: Date.now(),
    ...(detail ? { detail } : {})
  })
  writeDeviceProfiles(next)
  const isNew = !saved.some(profile => profile.id === id)
  if (
    kind === 'student' &&
    isNew &&
    next.length > 1 &&
    !hasSeenProfilesNotice(id)
  ) {
    setCookieOnDomain(DEVICE_NOTICE_PENDING_COOKIE, id, DEVICE_PROFILES_DAYS)
  }
}

export const forgetCurrentDeviceProfile = (): void => {
  const id = currentDeviceProfileId()
  if (!id) return
  writeDeviceProfiles(readDeviceProfiles().filter(item => item.id !== id))
  if (readUnsavedProfileId() === id) removeCookieOnDomain(DEVICE_UNSAVED_COOKIE)
}

export const touchDeviceLastUse = (now: number = Date.now()): void => {
  setCookieOnDomain(DEVICE_LAST_USE_COOKIE, String(now), DEVICE_PROFILES_DAYS)
}

export const currentDeviceIdleAction = (
  now: number = Date.now()
): DeviceIdleAction =>
  resolveDeviceIdleAction({
    now,
    lastUse: parseLastUse(getCookie(DEVICE_LAST_USE_COOKIE)),
    profiles: readDeviceProfiles(),
    currentId: currentDeviceProfileId(),
    unsavedId: readUnsavedProfileId()
  })
